import React, { useState } from 'react';

import { classnames } from '@/utils/classnames.ts';

export interface AvatarProps extends React.ComponentPropsWithRef<'span'> {
    name: string;
    src?: string;
    // oklch hue (0–359) the avatar is tinted with; without one it stays grey.
    hue?: number | null;
    // 2xs sits among 16px icons, e.g. in the rail. `header` in a page's header (Page.Media): `md` beside the title, larger
    // above it on phones.
    size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'header';
    // Shown instead of the initials when there's no image, e.g. for a bot.
    icon?: React.ReactNode;
}

const getInitials = (name: string) =>
    name
        .split(/\s+/)
        // Skips words like '&' or '–', so 'Privat & Freelance' becomes 'PF'.
        .filter((word) => /^[\p{L}\p{N}]/u.test(word))
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase())
        .join('');

const Avatar = ({ name, src, hue, size = 'md', icon, className, style, ...props }: AvatarProps) => {
    // Remembers which src failed, so a new src gets tried again.
    const [failedSrc, setFailedSrc] = useState<string>();
    const imageSrc = src !== failedSrc ? src : undefined;
    const isColored = !imageSrc && typeof hue === 'number';
    // The tint is mixed with the surface rather than see-through, so a line behind the avatar (a timeline's) stops at it.

    return (
        <span
            {...props}
            style={isColored ? ({ '--avatar-hue': hue, ...style } as React.CSSProperties) : style}
            className={classnames(
                // Its edge a shade of the text's colour, darker in the light theme and lighter in the dark one, so a logo
                // on white doesn't run into what's around it, while a photo's edge only deepens rather than getting a
                // light line; drawn over the image rather than around it, so the image keeps its size.
                'relative inline-flex shrink-0 items-center justify-center overflow-hidden bg-surface-muted font-semibold text-text-muted select-none after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-text/8',
                isColored &&
                    'bg-[color-mix(in_oklab,oklch(70%_0.15_var(--avatar-hue))_20%,var(--color-surface))] text-[oklch(50%_0.15_var(--avatar-hue))] dark:text-[oklch(90%_0.2_var(--avatar-hue))]',
                size === '2xs' && 'size-5 rounded-md text-2xs',
                size === 'xs' && 'size-6 rounded-md text-2xs',
                size === 'sm' && 'size-8 rounded-lg text-xs',
                size === 'md' && 'size-10 rounded-xl text-sm',
                size === 'lg' && 'size-13 rounded-2xl text-base',
                size === 'header' &&
                    'size-10 rounded-xl text-sm max-sm:size-16 max-sm:rounded-[1.25rem] max-sm:text-lg',
                className
            )}
        >
            {imageSrc ? (
                <img
                    src={imageSrc}
                    alt={name}
                    onError={() => setFailedSrc(imageSrc)}
                    className="size-full object-cover"
                />
            ) : icon ? (
                <span aria-label={name} className="flex size-1/2 items-center justify-center [&>svg]:size-full">
                    {icon}
                </span>
            ) : (
                <span aria-label={name}>{getInitials(name)}</span>
            )}
        </span>
    );
};

export default Avatar;
